import React from 'react';

const DoshaWheel = ({ scores, size = 200 }) => {
  const { vata, pitta, kapha } = scores;
  const centerX = size / 2;
  const centerY = size / 2;
  const radius = size / 2 - 20;

  // Calculate angles for each dosha segment
  const vataDegrees = (vata / 100) * 360;
  const pittaDegrees = (pitta / 100) * 360;
  const kaphaDegrees = (kapha / 100) * 360;

  // Create SVG paths for each dosha segment
  const createArcPath = (startAngle, endAngle, innerRadius = 0) => {
    const start = polarToCartesian(centerX, centerY, radius, endAngle);
    const end = polarToCartesian(centerX, centerY, radius, startAngle);
    const innerStart = polarToCartesian(centerX, centerY, innerRadius, endAngle);
    const innerEnd = polarToCartesian(centerX, centerY, innerRadius, startAngle);

    const largeArcFlag = endAngle - startAngle <= 180 ? "0" : "1";

    return [
      "M", start.x, start.y, 
      "A", radius, radius, 0, largeArcFlag, 0, end.x, end.y,
      innerRadius > 0 ? "L" : "",
      innerRadius > 0 ? innerEnd.x : "",
      innerRadius > 0 ? innerEnd.y : "",
      innerRadius > 0 ? "A" : "",
      innerRadius > 0 ? innerRadius : "",
      innerRadius > 0 ? innerRadius : "",
      innerRadius > 0 ? 0 : "",
      innerRadius > 0 ? largeArcFlag : "",
      innerRadius > 0 ? 1 : "",
      innerRadius > 0 ? innerStart.x : "",
      innerRadius > 0 ? innerStart.y : "",
      "Z"
    ].filter(Boolean).join(" ");
  };

  const polarToCartesian = (centerX, centerY, radius, angleInDegrees) => {
    const angleInRadians = (angleInDegrees - 90) * Math.PI / 180.0;
    return {
      x: centerX + (radius * Math.cos(angleInRadians)),
      y: centerY + (radius * Math.sin(angleInRadians))
    };
  };

  // Calculate label positions
  const getLabelPosition = (startAngle, endAngle) => {
    const midAngle = startAngle + (endAngle - startAngle) / 2;
    const labelRadius = radius * 0.7;
    return polarToCartesian(centerX, centerY, labelRadius, midAngle);
  };

  // Dosha colors matching the CSS design system
  const doshaColors = {
    vata: '#3b82f6',  // Primary blue - matches .dosha-vata border
    pitta: '#10b981', // Secondary green - matches .dosha-pitta border
    kapha: '#06b6d4'  // Accent cyan - matches .dosha-kapha border
  };

  let currentAngle = 0;
  const segments = [
    { name: 'Vata', value: vata, color: doshaColors.vata, degrees: vataDegrees },
    { name: 'Pitta', value: pitta, color: doshaColors.pitta, degrees: pittaDegrees },
    { name: 'Kapha', value: kapha, color: doshaColors.kapha, degrees: kaphaDegrees }
  ];

  // Check if all values are very small or zero
  const totalValue = vata + pitta + kapha;
  const hasValidData = totalValue > 0;

  // Use simple circular segments approach
  const circumference = 2 * Math.PI * (radius - 10);
  const vataDashArray = `${(vata / 100) * circumference} ${circumference}`;
  const pittaDashArray = `${(pitta / 100) * circumference} ${circumference}`;
  const kaphaDashArray = `${(kapha / 100) * circumference} ${circumference}`;

  // Calculate offsets for proper positioning
  const vataOffset = 0;
  const pittaOffset = -(vata / 100) * circumference;
  const kaphaOffset = -((vata + pitta) / 100) * circumference;

  return (
    <div className="flex flex-col items-center">
      <div className="relative">
        <svg width={size} height={size} className="drop-shadow-lg">
          {/* Background circle */}
          <circle
            cx={centerX}
            cy={centerY}
            r={radius}
            fill="#f3f4f6"
            stroke="#e5e7eb"
            strokeWidth="2"
          />

          {/* Dosha segments using stroke-dasharray */}
          {hasValidData && (
            <>
              {/* Vata segment */}
              {vata > 0 && (
                <circle
                  cx={centerX}
                  cy={centerY}
                  r={radius - 10}
                  fill="none"
                  stroke={doshaColors.vata}
                  strokeWidth="20"
                  strokeDasharray={vataDashArray}
                  strokeDashoffset={vataOffset}
                  transform={`rotate(-90 ${centerX} ${centerY})`}
                />
              )}
              
              {/* Pitta segment */}
              {pitta > 0 && (
                <circle
                  cx={centerX}
                  cy={centerY}
                  r={radius - 10}
                  fill="none"
                  stroke={doshaColors.pitta}
                  strokeWidth="20"
                  strokeDasharray={pittaDashArray}
                  strokeDashoffset={pittaOffset}
                  transform={`rotate(-90 ${centerX} ${centerY})`}
                />
              )}
              
              {/* Kapha segment */}
              {kapha > 0 && (
                <circle
                  cx={centerX}
                  cy={centerY}
                  r={radius - 10}
                  fill="none"
                  stroke={doshaColors.kapha}
                  strokeWidth="20"
                  strokeDasharray={kaphaDashArray}
                  strokeDashoffset={kaphaOffset}
                  transform={`rotate(-90 ${centerX} ${centerY})`}
                />
              )}
            </>
          )}

          {/* Center circle */}
          <circle
            cx={centerX}
            cy={centerY}
            r={30}
            fill="white"
            stroke="#e5e7eb"
            strokeWidth="2"
          />
          
          {/* Center text */}
          <text
            x={centerX}
            y={centerY - 5}
            textAnchor="middle"
            className="fill-gray-600 font-semibold text-xs"
          >
            Your
          </text>
          <text
            x={centerX}
            y={centerY + 8}
            textAnchor="middle"
            className="fill-gray-800 font-bold text-xs"
          >
            Dosha
          </text>
        </svg>
      </div>

      {/* Legend */}
      <div className="mt-6 flex flex-wrap justify-center gap-4">
        <div className="flex items-center space-x-2">
          <div
            className="w-4 h-4 rounded-full"
            style={{ backgroundColor: doshaColors.vata }}
          ></div>
          <span className="text-sm font-medium text-gray-700">
            Vata: {vata}%
          </span>
        </div>
        <div className="flex items-center space-x-2">
          <div
            className="w-4 h-4 rounded-full"
            style={{ backgroundColor: doshaColors.pitta }}
          ></div>
          <span className="text-sm font-medium text-gray-700">
            Pitta: {pitta}%
          </span>
        </div>
        <div className="flex items-center space-x-2">
          <div
            className="w-4 h-4 rounded-full"
            style={{ backgroundColor: doshaColors.kapha }}
          ></div>
          <span className="text-sm font-medium text-gray-700">
            Kapha: {kapha}%
          </span>
        </div>
      </div>
    </div>
  );
};

export default DoshaWheel;
